// src/component/datatable2/models/ActiveFilterChips.ts

import { FILTER_TYPES } from '../constants/FilterTypes';
import type { CriteriaFilter } from './FilterCriteriaModel';

/** One removable chip above the table ("Name: value"). */
export interface ActiveFilterChip {
    /** Field name of the filter. */
    name: string;
    /** Column label of the filter. */
    nameLabel: string | undefined;
    /** The removed value (one option of a list) or the whole filter value. */
    value: any;
    valueLabel: string;
}

export interface ActiveFilterChipFormatters {
    t: (key: string) => string;
    formatDate: (date: unknown, withTime: boolean) => string;
    formatBoolean: (value: unknown) => string;
}

/** Turns the active filters into the chips shown above the table. */
export class ActiveFilterChips {
    constructor(private readonly formatters: ActiveFilterChipFormatters) {}

    build(filters: Record<string, CriteriaFilter> | undefined | null): ActiveFilterChip[] {
        const result: ActiveFilterChip[] = [];

        if (!filters) {
            return result;
        }

        Object.keys(filters).forEach(fieldName => {
            result.push(...this.chipsOf(fieldName, filters[fieldName]));
        });

        return result;
    }

    private chipsOf(name: string, filter: CriteriaFilter): ActiveFilterChip[] {
        const { t, formatDate, formatBoolean } = this.formatters;
        const nameLabel = filter.filterNameLabel;
        const whole = (valueLabel: string): ActiveFilterChip[] => [{ name, nameLabel, value: filter.value, valueLabel }];
        const value = filter.value;

        switch (filter.type) {
            case FILTER_TYPES.LIST:
                return (value ?? []).map((item: any) => ({ name, nameLabel, value: item, valueLabel: item.label }));

            case FILTER_TYPES.TEXT:
                if (Array.isArray(value)) {
                    return value.map((item: any) => ({
                        name,
                        nameLabel,
                        value: item,
                        valueLabel: item.label ?? item.id ?? String(item),
                    }));
                }

                return value ? whole(value) : [];

            case FILTER_TYPES.DATE_RANGE:
            case FILTER_TYPES.DATE_TIME_RANGE: {
                const withTime = filter.type === FILTER_TYPES.DATE_TIME_RANGE;

                if (!value?.from && !value?.to) {
                    return [];
                }

                if (value.from && value.to) {
                    return whole(`${formatDate(value.from, withTime)} - ${formatDate(value.to, withTime)}`);
                }

                return value.from
                    ? whole(`${t('filterPanel.dateFrom')} ${formatDate(value.from, withTime)}`)
                    : whole(`${t('filterPanel.dateTo')} ${formatDate(value.to, withTime)}`);
            }

            case FILTER_TYPES.NUMBER_RANGE:
            case FILTER_TYPES.AMOUNT:
                if (value?.from == null && value?.to == null) {
                    return [];
                }

                if (value.from != null && value.to != null) {
                    return whole(`${value.from} - ${value.to}`);
                }

                return value.from != null
                    ? whole(`${t('filterPanel.numberFrom')} ${value.from}`)
                    : whole(`${t('filterPanel.numberTo')} ${value.to}`);

            case FILTER_TYPES.BOOLEAN:
                return whole(formatBoolean(value));

            default:
                return [];
        }
    }
}
